import { useEffect, useState } from "react";
import type { OverlayState, SatLayer } from "../../state/store";
import type { EngineStatus } from "./frameEngine";
import { FUTURE_CHANCE_IN, FUTURE_MODELS, type FrameSet, type FutureModel, type FutureProduct, type Legend, type OceanLayer } from "./providers";

// ── the satellite / radar card (top-left of the map) ──

const SAT_OPTIONS: { k: SatLayer; label: string; title: string }[] = [
  { k: "off", label: "Off", title: "No satellite" },
  { k: "geocolor", label: "GeoColor", title: "True colour by day, city lights + IR clouds by night (NASA GIBS full disk, 10-min)" },
  { k: "ir", label: "IR", title: "Enhanced infrared — cloud-top temperature, day and night. 5-min rapid feed where NOAA nowCOAST covers the view, GIBS full disk elsewhere" },
  { k: "wv", label: "WV", title: "Water vapour — mid/upper-level moisture, dry air and steering flow (nowCOAST, 5-min)" },
  { k: "vis", label: "Vis", title: "High-resolution visible (0.5 km) — eye and cloud texture, daylight only" },
];
const OCEAN_OPTIONS: { k: OceanLayer; label: string; title: string }[] = [
  { k: "off", label: "Off", title: "No ocean layer" },
  { k: "sst", label: "SST", title: "Sea-surface temperature (NASA SPoRT, 2 km, 6-hourly)" },
  { k: "ohc", label: "Heat", title: "Ocean heat content — how deep the warm water runs (NOAA AOML, daily)" },
];
const SPEEDS = [0.5, 1, 2];
const LOOPS = [60, 90, 180];

function ago(ms: number | null | undefined, now: number): string | null {
  if (ms == null) return null;
  const m = Math.round((now - ms) / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min old`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min old`;
}
const zTime = (ms: number) => {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}Z`;
};
const localTime = (ms: number) =>
  new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
const archiveStamp = (ms: number) =>
  new Date(ms).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) + ` ${zTime(ms)}`;

function LegendBar({ legend }: { legend: Legend }) {
  // IR runs warm → cold left to right (colder = more intense); dBZ low → high
  const desc = legend.unit === "°C";
  const stops = [...legend.stops].sort((a, b) => (desc ? b.v - a.v : a.v - b.v));
  const f = (v: number) => (legend.log ? Math.log10(v) : v);
  const first = f(stops[0].v), last = f(stops[stops.length - 1].v);
  const pos = (v: number) => ((f(v) - first) / (last - first)) * 100;
  const grad = stops.map((s) => `${s.color} ${pos(s.v)}%`).join(", ");
  const every = Math.ceil(stops.length / 5);
  const ticks = stops.filter((_, i) => i % every === 0 || i === stops.length - 1)
    .filter((s, i, arr) => i === arr.length - 1 || Math.abs(pos(arr[arr.length - 1].v) - pos(s.v)) > 12);
  return (
    <div className="ovl-legend" title={legend.note}>
      <div className="ovl-legend-bar" style={{ background: `linear-gradient(90deg, ${grad})` }} />
      <div className="ovl-legend-ticks">
        {ticks.map((s, i) => (
          <span
            key={s.v}
            style={{ left: `${pos(s.v)}%`, transform: `translateX(${i === 0 ? 0 : i === ticks.length - 1 ? -100 : -50}%)` }}
          >
            {s.v}
          </span>
        ))}
      </div>
      <div className="ovl-legend-note">{legend.unit}{legend.note ? ` · ${legend.note}` : ""}</div>
    </div>
  );
}

interface Props {
  compact?: boolean; // narrow screens: one pill naming what's on, opening into the card
  overlays: OverlayState;
  radar: FrameSet | null;
  sat: FrameSet | null;
  satBase: FrameSet | null; // global WV mosaic filling in outside the GOES sectors
  ocean: FrameSet | null;
  lightning: FrameSet | null;
  meso: FrameSet | null;
  mesoNote: string | null; // why there is no 1-min loop (or that one is being found)
  mesoPossible: boolean; // a live storm is selected
  futurePossible: boolean; // a live storm is selected, or an NHC outlook area is playing
  // the NHC outlook area the futurecast plays (no storm open), and closing it
  futureArea: { title: string; prob7: number | null; color: string } | null;
  onCloseFutureArea: () => void;
  future: FrameSet | null; // futurecast frames (a model's simulated IR / radar)
  futureNote: string | null;
  futureNoteText: string | null; // a caveat for this product from this model (ECMWF's stand-ins)
  toggleFuture: () => void;
  setFutureProduct: (p: FutureProduct) => void;
  setFutureModel: (m: FutureModel) => void;
  setFutureChanceIn: (v: number) => void;
  status: EngineStatus | null;
  archive: boolean; // radar is showing the NEXRAD archive (replay on screen)
  setSatellite: (s: SatLayer) => void;
  setOcean: (v: OceanLayer) => void;
  toggleLightning: () => void;
  toggleMeso: () => void;
  toggleRadar: () => void;
  setOpacity: (v: number) => void;
  toggleMotion: () => void;
  setSpeed: (v: number) => void;
  setLoopMin: (v: number) => void;
  onTogglePlay: () => void;
  onStep: (d: 1 | -1) => void;
  onSeek: (i: number) => void;
}

export default function OverlayControl(p: Props) {
  const { overlays, radar, sat, ocean, lightning, meso, status } = p;
  const animated = overlays.satellite !== "off" || overlays.radar || overlays.lightning || overlays.meso;
  const any = animated || overlays.ocean !== "off";
  // tick the "N min old" labels
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!any) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [any]);
  const [open, setOpen] = useState(false); // compact: the card is open

  if (p.compact && !open) {
    const on = [
      overlays.satellite !== "off" ? SAT_OPTIONS.find((o) => o.k === overlays.satellite)?.label : null,
      overlays.radar ? "Radar" : null,
      overlays.ocean !== "off" ? OCEAN_OPTIONS.find((o) => o.k === overlays.ocean)?.label : null,
      overlays.lightning ? "Lightning" : null,
      overlays.meso ? "1-min" : null,
      overlays.future ? `🔮 ${p.future?.label.split(" ")[0] ?? "Futurecast"}` : null,
    ].filter(Boolean);
    return (
      <button className={on.length ? "ovl-pill on" : "ovl-pill"} onClick={() => setOpen(true)} aria-expanded={false}>
        🛰️ {on.length ? on.join(" · ") : "Imagery"} ▾
      </button>
    );
  }

  const satNewest = sat?.frames.at(-1)?.time ?? null;
  const baseNewest = p.satBase?.frames.at(-1)?.time ?? null;
  const radarNewest = radar?.frames.at(-1)?.time ?? null;
  const mesoNewest = meso?.frames.at(-1)?.time ?? null;
  const ltgNewest = lightning?.frames.at(-1)?.time ?? null;
  const motionOn = overlays.motion && !p.archive;
  const shownT = status?.time ?? null;

  return (
    <div className="ovl-card">
      {p.compact && <button className="ovl-done" onClick={() => setOpen(false)} aria-expanded>Done ▴</button>}
      <div className="ovl-row">
        <span className="ovl-k">🛰️ SAT</span>
        <div className="ovl-seg">
          {SAT_OPTIONS.map((o) => (
            <button key={o.k} title={o.title} className={overlays.satellite === o.k ? "on" : ""} onClick={() => p.setSatellite(o.k)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      {overlays.satellite !== "off" && (
        <div className="ovl-src">
          {sat ? (
            <>
              <b>{sat.label}</b> · {sat.detail}
              {satNewest != null && (
                <span className={now - satNewest > 30 * 60_000 ? "ovl-age stale" : "ovl-age"}> · {ago(satNewest, now)}</span>
              )}
            </>
          ) : "loading…"}
          {p.satBase && (
            <div
              className="muted"
              title="GOES water vapor only covers the US sectors (about 15–50°N). Outside them, NOAA/NESDIS's global satellite mosaic fills in, recoloured to match."
            >
              + beyond GOES: <b>{p.satBase.label}</b> · hourly
              {baseNewest != null && (
                <span className={now - baseNewest > 180 * 60_000 ? "ovl-age stale" : "ovl-age"}> · {ago(baseNewest, now)}</span>
              )}
            </div>
          )}
        </div>
      )}

      {p.mesoPossible && (
        <div className="ovl-row">
          <button
            className={overlays.meso ? "ovl-toggle on" : "ovl-toggle"} onClick={p.toggleMeso}
            title="1-minute GOES mesoscale imagery over the storm (IR, or the WV / Vis product picked above)"
          >
            ⏱ 1-min storm loop
          </button>
          {overlays.meso && (
            <span className="ovl-src inline">
              {meso ? (
                <>
                  <b>{meso.label}</b> · {meso.detail}
                  {mesoNewest != null && <span className="ovl-age"> · {ago(mesoNewest, now)}</span>}
                </>
              ) : p.mesoNote ?? "loading…"}
            </span>
          )}
        </div>
      )}

      {p.futurePossible && (
        <div className="ovl-row">
          <button
            className={overlays.future ? "ovl-toggle on future" : "ovl-toggle"} onClick={p.toggleFuture}
            title="Futurecast: forecast models' simulated satellite / radar / rain along the storm's future path, and the official rainfall forecast. Model scenarios, not observations."
          >
            🔮 Futurecast
          </button>
          {overlays.future && (
            <div className="ovl-seg future-products" style={{ flex: "0 0 auto", minWidth: 196 }}>
              {(["ir", "radar", "rain", "chance"] as const).map((k) => (
                <button
                  key={k} className={overlays.futureProduct === k ? "on" : ""} onClick={() => p.setFutureProduct(k)}
                  title={k === "rain" ? "Rain that falls from now along the path, with town totals — tap the map for the total at any spot"
                    : k === "chance" ? "NOAA NBM: the chance of more than 1–10 inches in each period (US) — tap the map for a spot's chances"
                      : undefined}
                >
                  {k === "ir" ? "IR" : k === "radar" ? "Radar" : k === "rain" ? "Rain" : "Chance"}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {p.futurePossible && overlays.future && p.futureArea && (
        <div className="ovl-src future-area">
          <span>
            for NHC outlook area <b>{p.futureArea.title}</b>
            {p.futureArea.prob7 != null && (
              <> · <b style={{ color: p.futureArea.color }}>{p.futureArea.prob7}%</b> in 7 days</>
            )}
          </span>
          <button className="future-area-x" onClick={p.onCloseFutureArea} title="Close this area's futurecast">✕</button>
        </div>
      )}
      {p.futurePossible && overlays.future && (
        <div className="ovl-row">
          <div className="ovl-seg future-models">
            {FUTURE_MODELS.map((m) => (
              <button
                key={m.k} title={m.title} onClick={() => p.setFutureModel(m.k)}
                className={(overlays.futureModel === m.k ? "on" : "") + (m.blend ? " blend" : m.rainOnly ? " official" : "")}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {p.futurePossible && overlays.future && overlays.futureProduct === "chance" && (
        <div className="ovl-row">
          <span className="ovl-k" title="The chance of more than this much rain in each period">MORE THAN</span>
          <div className="ovl-seg future-chance">
            {FUTURE_CHANCE_IN.map((t) => (
              <button key={t} className={overlays.futureChanceIn === t ? "on" : ""} onClick={() => p.setFutureChanceIn(t)}>
                {t}″
              </button>
            ))}
          </div>
        </div>
      )}
      {p.futurePossible && overlays.future && (
        <div className="ovl-src">
          {p.future ? (
            <>
              <b>{p.future.label}</b> · {p.future.detail}
              {status?.future && status.future.ready < status.future.total && (
                <span className="ovl-age"> · rendering {status.future.ready}/{status.future.total}</span>
              )}
              {p.futureNote && <div className="muted">{p.futureNote}</div>}
              {p.futureNoteText && <div className="muted">⚠️ {p.futureNoteText}</div>}
            </>
          ) : p.futureNote ?? "loading…"}
          {p.future?.legend && !(overlays.satellite !== "off" && p.future.legend === sat?.legend) && <LegendBar legend={p.future.legend} />}
        </div>
      )}

      <div className="ovl-row">
        <button className={overlays.radar ? "ovl-toggle on" : "ovl-toggle"} onClick={p.toggleRadar} title="NEXRAD radar (hi-res over the US), global mosaic elsewhere">
          📡 Radar
        </button>
        {overlays.radar && (
          <span className="ovl-src inline">
            {radar ? (
              <>
                <b>{radar.label}</b> · {radar.detail}
                {p.archive
                  // the hour actually on screen (the next one may still be loading)
                  ? (status?.shownTime.radar ?? radarNewest) != null && <> · {archiveStamp((status?.shownTime.radar ?? radarNewest)!)}</>
                  : radarNewest != null && <span className="ovl-age"> · {ago(radarNewest, now)}</span>}
              </>
            ) : "loading…"}
          </span>
        )}
      </div>

      <div className="ovl-row">
        <span className="ovl-k">🌊 OCEAN</span>
        <div className="ovl-seg" style={{ flex: "0 0 auto", minWidth: 132 }}>
          {OCEAN_OPTIONS.map((o) => (
            <button key={o.k} title={o.title} className={overlays.ocean === o.k ? "on" : ""} onClick={() => p.setOcean(o.k)}>
              {o.label}
            </button>
          ))}
        </div>
        <button
          className={overlays.lightning ? "ovl-toggle on" : "ovl-toggle"} onClick={p.toggleLightning}
          title="Lightning strike density (NLDN + GLD360, 15-min) — over the ocean too"
        >
          ⚡ Lightning
        </button>
      </div>
      {(overlays.ocean !== "off" || overlays.lightning) && (
        <div className="ovl-src">
          {overlays.ocean !== "off" && (ocean ? <><b>{ocean.label}</b> · {ocean.detail}</> : "ocean layer loading…")}
          {overlays.ocean !== "off" && overlays.lightning && " · "}
          {overlays.lightning && (lightning
            ? <><b>Lightning</b>{ltgNewest != null && <span className="ovl-age"> · {ago(ltgNewest, now)}</span>}</>
            : "lightning loading…")}
        </div>
      )}

      {any && (
        <>
          <div className="ovl-row">
            <span className="ovl-k" title="Overlay opacity">◐</span>
            <input
              type="range" min={0.2} max={1} step={0.05} value={overlays.opacity}
              onChange={(e) => p.setOpacity(Number(e.target.value))}
              title="Overlay opacity" className="ovl-range"
            />
          </div>

          {animated && <div className="ovl-row">
            <button
              className={motionOn ? "ovl-toggle on" : "ovl-toggle"}
              onClick={p.toggleMotion}
              disabled={p.archive}
              title={p.archive ? "Archive radar follows the replay's TimeDock" : "Animate the recent loop"}
            >
              {motionOn ? "Loop: on" : "▶ Loop"}
            </button>
            {motionOn && status && status.count >= 2 && (
              <>
                <button className="ovl-icon" onClick={() => p.onStep(-1)} title="Previous frame">⏮</button>
                <button className="ovl-icon" onClick={p.onTogglePlay} title={status.playing ? "Pause" : "Play"}>
                  {status.playing ? "⏸" : "▶"}
                </button>
                <button className="ovl-icon" onClick={() => p.onStep(1)} title="Next frame">⏭</button>
                <button
                  className="ovl-icon wide"
                  onClick={() => p.setSpeed(SPEEDS[(SPEEDS.indexOf(overlays.speed) + 1) % SPEEDS.length])}
                  title="Playback speed"
                >
                  {overlays.speed}×
                </button>
                <select
                  className="ovl-select" value={overlays.loopMin}
                  onChange={(e) => p.setLoopMin(Number(e.target.value))} title="Loop length"
                >
                  {LOOPS.map((m) => <option key={m} value={m}>{m >= 120 ? `${m / 60} h` : `${m} min`}</option>)}
                </select>
              </>
            )}
          </div>}

          {motionOn && status && status.count >= 2 && (
            <>
              <input
                type="range" min={0} max={status.count - 1} value={status.index}
                onChange={(e) => p.onSeek(Number(e.target.value))}
                className="ovl-range ovl-scrub" title="Scrub the loop (pauses)"
              />
              <div className="ovl-time">
                {shownT != null && (
                  <>
                    <b>{zTime(shownT)}</b> <span className="muted">· {localTime(shownT)}</span>
                    {status.index < status.count - 1 && (
                      <span className="muted"> · −{Math.round(((radarNewest ?? satNewest ?? shownT) - shownT) / 60_000)} min</span>
                    )}
                  </>
                )}
                {status.buffering && (
                  <span className="ovl-buffer">
                    loading {status.ready}/{status.total}
                    <span className="ovl-buffer-bar"><span style={{ width: `${(status.ready / Math.max(1, status.total)) * 100}%` }} /></span>
                  </span>
                )}
              </div>
            </>
          )}

          {radar?.legend && overlays.radar && <LegendBar legend={radar.legend} />}
          {sat?.legend && overlays.satellite !== "off" && <LegendBar legend={sat.legend} />}
          {/* the 1-min loop shares the satellite's colour table when both show the same product */}
          {meso?.legend && overlays.meso && meso.legend !== sat?.legend && <LegendBar legend={meso.legend} />}
          {lightning?.legend && overlays.lightning && <LegendBar legend={lightning.legend} />}
          {ocean?.legend && overlays.ocean !== "off" && <LegendBar legend={ocean.legend} />}
        </>
      )}
    </div>
  );
}
